/**
 * Medicare Cost Baseline for SAFECARE Model
 * 
 * This baseline represents the "efficient cost" of healthcare delivery
 * based on Medicare payment rates and actual costs, NOT the inflated
 * commercial rates in the current system.
 * 
 * Data Sources:
 * - 2024 Medicare Trustees Report (CMS)
 * - Medicare Payment Advisory Commission (MedPAC) Reports
 * - CMS National Health Expenditure Accounts
 * - Actuarial age-cost curves from Society of Actuaries
 */

export const MedicareBaseline = {

    // ========================================================================
    // MEDICARE COST PER BENEFICIARY (2024)
    // ========================================================================
    // Source: 2024 Medicare Trustees Report, Table II.D1 (HI and SMI Average per Beneficiary Costs)
    // Link: https://www.cms.gov/oact/tr/2024
    // Note: Figures include Part A, Part B, and Part D.
    // Average Medicare spending per beneficiary (Part A + Part B + Part D)

    costPerBeneficiary: {
        2024: 16570, // $16,570 per beneficiary (all parts)
        2025: 17420, // Projected
        2026: 18310,
        2027: 19240,
        2028: 20220,
        2029: 21250,
        2030: 22330,
        2031: 23470,
        2032: 24670,
        2033: 25930,
        2034: 27260,
        2035: 28650, // Projected ~5.1%
        2036: 30110,
        2037: 31650,
        2038: 33260,
        2039: 34960
        // Growth rate: ~5.1% per year (medical inflation)
    },

    // ========================================================================
    // AGE-COST ADJUSTMENT FACTORS
    // ========================================================================
    // Medicare population (65+) is more expensive than working-age population
    // These factors adjust Medicare costs to reflect younger, healthier populations
    // Source: Society of Actuaries, CMS actuarial data

    ageCostFactors: {
        // Relative cost compared to average Medicare beneficiary (age 65+)
        // Factor < 1.0 means less expensive, > 1.0 means more expensive

        '0-17': 0.25,    // Children: 25% of Medicare cost
        '18-34': 0.35,   // Young adults: 35%
        '35-44': 0.45,   // Middle age: 45%
        '45-54': 0.65,   // Pre-Medicare: 65%
        '55-64': 0.85,   // Near-Medicare: 85%
        '65+': 1.00      // Medicare baseline
    },

    // ========================================================================
    // POPULATION DISTRIBUTION (2024 Census)
    // ========================================================================
    // Source: US Census Bureau, 2024 Population Estimates

    populationByAge: {
        '0-17': 0.22,    // 22% of population
        '18-34': 0.21,   // 21%
        '35-44': 0.13,   // 13%
        '45-54': 0.13,   // 13%
        '55-64': 0.13,   // 13%
        '65+': 0.18      // 18%
    },

    // ========================================================================
    // DYNAMIC EXTRAPOLATION HELPERS
    // ========================================================================

    getCostPerBeneficiary(year) {
        if (this.costPerBeneficiary[year]) return this.costPerBeneficiary[year];
        // Extrapolate using 5.1% annual growth (medical inflation)
        const lastYear = 2039;
        const lastVal = this.costPerBeneficiary[lastYear];
        return lastVal * Math.pow(1.051, year - lastYear);
    },

    getEstimatedPopulation(year) {
        if (this.totalPopulation[year]) return this.totalPopulation[year];
        // Extrapolate using 0.5% annual growth
        const lastYear = 2039;
        const lastVal = this.totalPopulation[lastYear];
        return Math.round(lastVal * Math.pow(1.005, year - lastYear));
    },

    // ========================================================================
    // WEIGHTED AVERAGE COST PER CAPITA (ALL AGES)
    // ========================================================================
    // Calculated by applying age-cost factors to population distribution

    calculateWeightedCostPerCapita(year) {
        const medicareCost = this.getCostPerBeneficiary(year);

        let weightedSum = 0;
        for (const ageGroup in this.populationByAge) {
            const popShare = this.populationByAge[ageGroup];
            const costFactor = this.ageCostFactors[ageGroup];
            weightedSum += popShare * costFactor;
        }

        // Weighted average cost factor across all ages
        // This will be < 1.0 because population is younger than Medicare
        const avgCostFactor = weightedSum;

        // Apply to Medicare cost
        return medicareCost * avgCostFactor;
    },

    // ========================================================================
    // SPENDING CATEGORY SHARES (Medicare Mix)
    // ========================================================================
    // Source: CMS National Health Expenditure Accounts, Medicare component
    // These shares reflect Medicare's actual spending distribution

    categoryShares: {
        hospital: 0.35,      // 35% (inpatient + outpatient)
        physician: 0.22,     // 22% (professional services)
        drug: 0.18,          // 18% (Part D)
        postAcute: 0.12,     // 12% (SNF, home health, hospice)
        other: 0.13          // 13% (DME, other)
    },

    // ========================================================================
    // ADMINISTRATIVE OVERHEAD
    // ========================================================================
    // Source: CMS administrative cost reports

    adminOverhead: {
        medicare: 0.02,      // 2% (actual Medicare overhead)
        safecare: 0.02,      // 2% (target for SAFECARE)
        currentSystem: 0.12  // 12% (current blended system with private insurance)
    },

    // ========================================================================
    // TOTAL POPULATION (2024)
    // ========================================================================
    // Source: US Census Bureau

    totalPopulation: {
        2024: 340000000,
        2025: 342000000,
        2026: 344000000,
        2027: 346000000,
        2028: 348000000,
        2029: 350000000,
        2030: 352000000,
        2031: 354000000,
        2032: 356000000,
        2033: 358000000,
        2034: 360000000,
        2035: 362000000,
        2036: 364000000,
        2037: 366000000,
        2038: 368000000,
        2039: 370000000
    },

    // ========================================================================
    // CALCULATE TOTAL NATIONAL SPENDING AT MEDICARE RATES
    // ========================================================================

    calculateNationalSpending(year, baselineData) {
        const medicareCost = this.getCostPerBeneficiary(year);

        // Get population from nhe_baseline instead of hardcoded
        const yearData = baselineData && baselineData.nheBaseline ? baselineData.nheBaseline.find(d => d.year === year) : null;
        const population = yearData ? yearData.population : this.getEstimatedPopulation(year);

        // Return null if year data is missing (shouldn't happen with extrapolation)
        if (!population || !medicareCost) {
            console.warn(`Medicare baseline data missing for year ${year}`);
            // Fallback extrapolation just in case
            return null;
        }

        const costPerCapita = this.calculateWeightedCostPerCapita(year);

        // Base medical spending (before admin)
        const baseMedicalSpending = population * costPerCapita;

        // Add administrative overhead (2%)
        const totalSpending = baseMedicalSpending * (1 + this.adminOverhead.safecare);

        return {
            year,
            population,
            costPerCapita,
            baseMedicalSpending,
            adminCost: baseMedicalSpending * this.adminOverhead.safecare,
            totalSpending,

            // Breakdown by category
            hospital: baseMedicalSpending * this.categoryShares.hospital,
            physician: baseMedicalSpending * this.categoryShares.physician,
            drug: baseMedicalSpending * this.categoryShares.drug,
            postAcute: baseMedicalSpending * this.categoryShares.postAcute,
            other: baseMedicalSpending * this.categoryShares.other
        };
    }
};

// ========================================================================
// COMPARISON: MEDICARE BASELINE VS CURRENT SYSTEM BASELINE
// ========================================================================

export function compareBaselines(year) {
    const medicareBaseline = MedicareBaseline.calculateNationalSpending(year);

    // Current system baseline (from nhe_baseline.json)
    // This includes commercial markups, high admin costs, high drug prices
    const currentSystemBaseline = {
        2027: 6551881200000,  // $6.55T
        2028: 7065523648000,  // $7.07T
        2029: 7627565435520,  // $7.63T
        2030: 8239889870208,  // $8.24T
        2031: 8904881673816,  // $8.90T
        2032: 9625267060999,  // $9.63T
        2033: 10404538375559, // $10.40T
        2034: 11245882751082, // $11.25T
        2035: 12156000000000, // Projected
        2036: 13140000000000,
        2037: 14205000000000,
        2038: 15355000000000,
        2039: 16599000000000
    };

    const currentSpending = currentSystemBaseline[year];
    const medicareSpending = medicareBaseline.totalSpending;

    const savings = currentSpending - medicareSpending;
    const savingsPercent = (savings / currentSpending) * 100;

    return {
        year,
        currentSystem: currentSpending,
        medicareRates: medicareSpending,
        savings,
        savingsPercent: savingsPercent.toFixed(1) + '%',

        breakdown: {
            eliminatedCommercialMarkup: currentSpending * 0.15, // ~15% markup
            reducedAdminCosts: currentSpending * (0.12 - 0.02), // 12% → 2%
            reducedDrugPrices: currentSpending * 0.18 * 0.50,   // 50% cut on 18% of spending
            otherSavings: savings - (currentSpending * 0.15) - (currentSpending * 0.10) - (currentSpending * 0.09)
        }
    };
}

// Example usage:
// const baseline2027 = MedicareBaseline.calculateNationalSpending(2027);
// console.log('2027 SAFECARE Spending at Medicare Rates:', baseline2027.totalSpending);
// console.log('Cost per capita:', baseline2027.costPerCapita);
//
// const comparison = compareBaselines(2027);
// console.log('Savings vs Current System:', comparison.savingsPercent);
